import { ChartBarSquareIcon } from '@heroicons/react/24/outline';
import { useState, type ComponentType, type ReactElement } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import {
  LwartLogo,
  LwartIcon,
  Squares2x2Icon,
  ChevronDownIcon,
  LogoutIcon,
} from '@shared/components';
import { cn } from '@shared/utils';

import { useAuth } from '@features/auth';

import FuelIcon from '../../assets/icons/fuel.svg?react';
import ReportsMenuIcon from '../../assets/icons/reports-menu.svg?react';

interface NavChild {
  path: string;
  label: string;
}

interface NavGroup {
  label: string;
  children: NavChild[];
}

interface NavTopItem {
  path: string;
  label: string;
}

interface NavGroupSectionProps {
  readonly group: NavGroup;
  readonly icon: ComponentType<{ className?: string }>;
  readonly collapsed: boolean;
  readonly isOpen: boolean;
  readonly currentPath: string;
  readonly onToggleCollapse: () => void;
  readonly onToggleGroup: () => void;
}

/**
 * Renders a collapsible sidebar nav group (icon + label header, expandable list of
 * child links). Shared by the "Gerenciar" and "Relatórios" groups in `AppLayout`,
 * which only differ by icon, label, and children.
 */
function NavGroupSection({
  group,
  icon: Icon,
  collapsed,
  isOpen,
  currentPath,
  onToggleCollapse,
  onToggleGroup,
}: NavGroupSectionProps): ReactElement {
  const isGroupActive = group.children.some((c) => currentPath.startsWith(c.path));

  return (
    <div>
      <div
        className={cn(
          'flex h-10 w-full items-center gap-3 px-5',
          isGroupActive && !collapsed ? 'border-r-4 border-login-btn-primary' : '',
        )}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          className="-ml-0.5 shrink-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-login-btn-primary focus-visible:ring-offset-1 rounded-sm"
        >
          <Icon className={cn('h-5 w-5', isGroupActive ? 'text-[#B2D34A]' : 'text-[#D5D7DA]')} />
        </button>

        {!collapsed ? (
          <button
            type="button"
            onClick={onToggleGroup}
            aria-expanded={isOpen}
            className="flex flex-1 items-center justify-between focus-visible:outline-hidden"
          >
            <span
              className={cn(
                'font-sans font-medium text-sm leading-5 tracking-normal',
                isGroupActive ? 'text-login-text-brand' : 'text-[#71767F]',
              )}
            >
              {group.label}
            </span>
            <ChevronDownIcon
              className={cn(
                'mr-4 h-5 w-5 shrink-0 transition-transform text-[#00467D]',
                isOpen ? 'rotate-180' : '',
              )}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>

      {isOpen ? (
        <div className="pl-5 pr-4">
          <ul>
            {group.children.map((child) => {
              const isActive = currentPath.startsWith(child.path);
              return (
                <li key={child.path} className="relative flex items-center">
                  <span
                    className={cn(
                      'absolute left-0 h-10 w-0.5 shrink-0',
                      isActive ? 'bg-login-border-brand' : 'bg-login-border-primary',
                    )}
                    aria-hidden="true"
                  />
                  <Link
                    to={child.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'flex h-10 w-full items-center pl-4 pr-2 font-sans font-medium text-sm leading-5 tracking-normal transition-colors',
                      isActive ? 'text-login-text-brand' : 'text-[#71767F]',
                    )}
                  >
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

const navTopItems: NavTopItem[] = [{ path: '/refuelings', label: 'Abastecimento' }];

const navGroups: NavGroup[] = [
  {
    label: 'Gerenciar',
    children: [
      { path: '/users', label: 'Usuários' },
      { path: '/vehicles', label: 'Veículos' },
      { path: '/drivers', label: 'Motoristas' },
      { path: '/fuel-stations', label: 'Postos/Fornecedores' },
    ],
  },
];

const navBottomGroups: NavGroup[] = [
  {
    label: 'Relatórios',
    children: [
      { path: '/reports/deviation', label: 'Desvio de padrão' },
      { path: '/reports/indicators', label: 'Indicadores operacionais' },
    ],
  },
];

export function AppLayout(): ReactElement {
  const location = useLocation();
  const { logout, isPending } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const group of [...navGroups, ...navBottomGroups]) {
      const isGroupActive = group.children.some((c) => location.pathname.startsWith(c.path));
      initial[group.label] = isGroupActive;
    }
    return initial;
  });

  function toggleGroup(label: string) {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="flex flex-1">
        <aside
          className={cn(
            'flex shrink-0 flex-col border-r border-[#F5F5F5] bg-white transition-[width] duration-200',
            collapsed ? 'w-16' : 'w-64',
          )}
          aria-label="Menu lateral"
        >
          <div className={cn('flex items-center px-5 py-6', collapsed ? 'justify-center' : '')}>
            <Link to="/dashboard" aria-label="Ir para o dashboard">
              {collapsed ? (
                <LwartIcon className="h-8 w-auto text-[#006CB7]" />
              ) : (
                <LwartLogo className="h-10 w-auto" />
              )}
            </Link>
          </div>

          <nav className="flex-1" aria-label="Navegação principal">
            {(() => {
              const isDashActive = location.pathname.startsWith('/dashboard');
              return (
                <Link
                  to="/dashboard"
                  aria-current={isDashActive ? 'page' : undefined}
                  className={cn(
                    'flex h-10 w-full items-center gap-3 px-5 font-sans font-medium text-sm leading-5 tracking-normal transition-colors',
                    isDashActive ? 'border-r-4 border-login-btn-primary' : '',
                  )}
                >
                  <ChartBarSquareIcon
                    className={cn(
                      'h-5 w-5 shrink-0',
                      isDashActive ? 'text-[#B2D34A]' : 'text-[#D5D7DA]',
                    )}
                  />
                  {!collapsed ? (
                    <span className={isDashActive ? 'text-[#003B6A]' : 'text-[#71767F]'}>
                      Dashboard
                    </span>
                  ) : null}
                </Link>
              );
            })()}

            {navGroups.map((group) => {
              const isGroupActive = group.children.some((c) =>
                location.pathname.startsWith(c.path),
              );
              const isOpen = (openGroups[group.label] ?? isGroupActive) && !collapsed;

              return (
                <NavGroupSection
                  key={group.label}
                  group={group}
                  icon={Squares2x2Icon}
                  collapsed={collapsed}
                  isOpen={isOpen}
                  currentPath={location.pathname}
                  onToggleCollapse={() => setCollapsed((v) => !v)}
                  onToggleGroup={() => toggleGroup(group.label)}
                />
              );
            })}

            {navTopItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex h-10 w-full items-center gap-3 px-5 font-sans font-medium text-sm leading-5 tracking-normal transition-colors',
                    isActive ? 'border-r-4 border-login-btn-primary' : '',
                  )}
                >
                  <FuelIcon
                    className={cn(
                      'h-5 w-5 shrink-0',
                      isActive ? 'text-[#B2D34A]' : 'text-[#D5D7DA]',
                    )}
                  />
                  {!collapsed ? (
                    <span className={isActive ? 'text-[#003B6A]' : 'text-[#71767F]'}>
                      {item.label}
                    </span>
                  ) : null}
                </Link>
              );
            })}

            {navBottomGroups.map((group) => {
              const isGroupActive = group.children.some((c) =>
                location.pathname.startsWith(c.path),
              );
              const isOpen = ((openGroups[group.label] ?? false) || isGroupActive) && !collapsed;

              return (
                <NavGroupSection
                  key={group.label}
                  group={group}
                  icon={ReportsMenuIcon}
                  collapsed={collapsed}
                  isOpen={isOpen}
                  currentPath={location.pathname}
                  onToggleCollapse={() => setCollapsed((v) => !v)}
                  onToggleGroup={() => toggleGroup(group.label)}
                />
              );
            })}
          </nav>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col bg-white pl-6 overflow-x-hidden overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <footer className="flex h-16 shrink-0 items-center border-t border-[#F5F5F5] bg-white">
        <div
          className={cn(
            'flex shrink-0 items-center px-6 transition-[width] duration-200',
            collapsed ? 'w-16' : 'w-64',
          )}
        >
          <button
            type="button"
            onClick={logout}
            disabled={isPending}
            className="flex items-center gap-2 transition-colors hover:opacity-70 disabled:opacity-50"
            aria-label="Sair da conta"
          >
            <LogoutIcon className="h-5 w-5" />
            {!collapsed ? (
              <span className="font-sans font-medium text-sm leading-5 tracking-normal text-[#71767F]">
                Sair da conta
              </span>
            ) : null}
          </button>
        </div>

        <div className="flex flex-1 items-center justify-between pl-12 pr-6">
          <p className="font-sans font-normal text-sm leading-5 tracking-normal text-[#71767F]">
            © {new Date().getFullYear()} Todos os direitos reservados.
          </p>

          <p className="font-sans font-normal text-sm leading-5 tracking-normal text-[#71767F]">
            Desenvolvido por{' '}
            <span className="font-semibold text-login-text-secondary">UDS Tecnologia</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
